import   React              ,
       { useEffect          ,
         useState          }  from "react";

import   classNames          from "classnames";

import   styles               from "./SubscriptionList.module.scss";

import   TabularDisplay       from "../../widget/dataDisplay/tables/TabularDisplay";
import   AnimatedWaitingIcon2 from "../../widget/animations/animatedWaitingIcon2";

import { groupData          }  from "../../../dataTypes/dataSetManagment/regroupData";

import { Subscription       }  from "../../../dataTypes/subscriptionManagment/types";

import { getSubscriptions   ,
         cancelSubscription  }  from "../../../backendServices/subscriptionManagment/api";

const BANK_LABELS: Record<string, string> = {
  '01': "Axepta" ,
  '02': "PayPal"  ,
  '03': "Stripe" ,
};

const STATUS_LABELS: Record<string, string> = {
  pending_approval : "En attente d'approbation" ,
  active           : "Actif"                    ,
  past_due         : "Paiement en échec"        ,
  canceled         : "Résilié"                  ,
};

const formatAmount = (amount: number): string => (amount / 100).toFixed(2) + " €";

const formatDate = (date: Date | null): string => date ? date.toLocaleString() : "—";

interface SubscriptionListProps {
  userEmail: string;
}

export const SubscriptionList: React.FC<SubscriptionListProps> = ({ userEmail }) => {

  const [ subscriptions   , setSubscriptions   ] = useState<Subscription[]>([]);
  const [ showWaitingIcon , setShowWaitingIcon ] = useState(false);
  const [ ready           , setReady           ] = useState(false);

  const fetchData = async () => {

    setShowWaitingIcon(true);

    const result = await getSubscriptions(userEmail);

    if (result.code === 1) {
      setSubscriptions(result.payload);
    }

    setShowWaitingIcon(false);
    setReady(true);
  };

  useEffect(() => {
    fetchData();
  }, [userEmail]);

  const handleCancel = async (subscription: Subscription) => {

    if (!window.confirm(`Résilier l'abonnement "${subscription.productVariantName ?? subscription.productName ?? ''}" ? Le quota déjà crédité n'est pas repris.`)) {
      return;
    }

    const result = await cancelSubscription(subscription.id);

    if (result.code !== 1) {
      window.alert(result.comment || "La résiliation n'a pas pu être demandée.");
      return;
    }

    window.alert(result.comment || "Résiliation demandée.");

    fetchData();
  };

  // TabularDisplay accède aux colonnes par une seule clé de l'objet — combiner
  // productName/productVariantName nécessite un accessor synthétique plutôt qu'un champ
  // réel de Subscription.
  const rows = subscriptions.map((s) => ({
    ...s,
    resourceLabel: [s.productName, s.productVariantName].filter(Boolean).join(" — ") || "—",
    // Composite plutôt que deux colonnes : la couleur vient du statut brut, le texte
    // doit en plus montrer le compteur d'échecs quand il y en a — un seul accessor ne
    // peut porter que l'un ou l'autre, cellRenderer ne reçoit que la valeur de la cellule.
    statusInfo: { status: s.status, failureCount: s.paymentFailureCount },
  }));

  type Row = typeof rows[number];

  const displayColumns = [
    { header: "Ressource"              , accessor: "resourceLabel"  as keyof Row , width: '260px' },
    {
      header  : "Banque" ,
      accessor: "bankCode" as keyof Row ,
      width   : '100px' ,
      cellRenderer: (value: any) => <span>{BANK_LABELS[value] ?? value ?? "—"}</span> ,
    },
    {
      header  : "Montant" ,
      accessor: "amount" as keyof Row ,
      width   : '100px' ,
      cellRenderer: (value: any) => <span>{formatAmount(value as number)}</span> ,
    },
    {
      header  : "Statut" ,
      accessor: "statusInfo" as keyof Row ,
      width   : '200px' ,
      cellRenderer: (value: any) => (
        <span className={classNames(styles.status, styles[`status_${value.status}`])}>
          {STATUS_LABELS[value.status] ?? value.status}
          {value.status === 'past_due' && value.failureCount > 0 ? ` (${value.failureCount})` : ''}
        </span>
      ) ,
    },
    {
      header  : "Créé le" ,
      accessor: "creationDate" as keyof Row ,
      width   : '150px' ,
      cellRenderer: (value: any) => <span>{formatDate(value)}</span> ,
    },
    {
      header  : "Dernier renouvellement" ,
      accessor: "lastRenewedDate" as keyof Row ,
      width   : '170px' ,
      cellRenderer: (value: any) => <span>{formatDate(value)}</span> ,
    },
  ];

  const groupedRows = groupData<Row>(rows, () => ({ key: '__all__' }));

  return (
    <div className={styles.root}>
      {showWaitingIcon && <AnimatedWaitingIcon2 />}

      {
       ready &&
       (
        subscriptions.length === 0
        ?
        (
         <div className={styles.noDataMessage}>
          Aucun abonnement en cours.
         </div>
        )
        :
        (
         <div className={styles.tableSection}>
           <TabularDisplay
             groupedData     = {groupedRows}
             columns         = {displayColumns}
             showGroupHeaders= {false}
             actionColumn    = {{
               header: "" ,
               width : '110px' ,
               renderer: (row: Row) => (
                 row.status === 'canceled'
                 ? null
                 : <button className={styles.cancelButton} onClick={() => handleCancel(row)}>Résilier</button>
               ) ,
             }}
           />
         </div>
        )
       )
      }
    </div>
  );
};

export default SubscriptionList;
